import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowTrendingUpIcon,
  ChatBubbleOvalLeftIcon,
  HeartIcon,
  EyeIcon,
  ClockIcon,
  ChevronRightIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  CalendarIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { FireIcon as FireSolidIcon, StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const PopularDiscussions = () => {
  const navigate = useNavigate();
  const [timeFilter, setTimeFilter] = useState('week');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('trending');
  const [loading, setLoading] = useState(true);
  const [discussions, setDiscussions] = useState([]);
  const [stats, setStats] = useState({});

  useEffect(() => {
    // Sample popular discussions data
    const discussionsData = [
      {
        id: 1,
        title: "New AI-Powered Diagnostic Tools: Revolutionary Impact on Medical Imaging",
        content: "Recent advances in artificial intelligence are transforming how we approach medical diagnostics...",
        author: {
          name: "Dr. Rajesh Kumar",
          avatar: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=50&h=50&fit=crop&crop=face",
          department: "Radiology",
          title: "Senior Radiologist"
        },
        forum: {
          id: 1,
          name: "Medical Research & Innovation",
          category: "medical"
        },
        createdAt: "2024-07-20T08:30:00Z",
        lastActivity: "2024-07-23T14:45:00Z",
        views: 2847,
        likes: 184,
        replies: 67,
        shares: 23,
        trendingScore: 95,
        isHot: true,
        isPinned: true,
        tags: ["AI", "Diagnostics", "Radiology", "Innovation"],
        image: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop"
      },
      {
        id: 2,
        title: "Mental Health Support Programs: Comprehensive Wellness Initiative for Healthcare Workers",
        content: "Addressing burnout and mental health challenges in healthcare through innovative support programs...",
        author: {
          name: "Dr. Lisa Rodriguez",
          avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=50&h=50&fit=crop&crop=face",
          department: "Psychiatry",
          title: "Chief of Mental Health"
        },
        forum: {
          id: 2,
          name: "Staff Wellness & Mental Health",
          category: "wellness"
        },
        createdAt: "2024-07-18T16:20:00Z",
        lastActivity: "2024-07-23T11:30:00Z",
        views: 2134,
        likes: 156,
        replies: 89,
        shares: 34,
        trendingScore: 87,
        isHot: true,
        isPinned: false,
        tags: ["Mental Health", "Wellness", "Support", "Healthcare Workers"],
        image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=250&fit=crop"
      },
      {
        id: 3,
        title: "Breakthrough in Cardiac Surgery: Minimally Invasive Robotic Procedures",
        content: "Revolutionary advances in robotic cardiac surgery are changing patient outcomes dramatically...",
        author: {
          name: "Dr. Sushma Iyer",
          avatar: "https://images.unsplash.com/photo-1594824388853-5a7a6b1ab4b9?w=50&h=50&fit=crop&crop=face",
          department: "Cardiac Surgery",
          title: "Lead Cardiac Surgeon"
        },
        forum: {
          id: 1,
          name: "Medical Research & Innovation",
          category: "medical"
        },
        createdAt: "2024-07-19T09:15:00Z",
        lastActivity: "2024-07-23T09:20:00Z",
        views: 1876,
        likes: 142,
        replies: 54,
        shares: 19,
        trendingScore: 82,
        isHot: true,
        isPinned: false,
        tags: ["Cardiac Surgery", "Robotics", "Minimally Invasive", "Innovation"],
        image: "https://images.unsplash.com/photo-1530026405186-ed1f139313f8?w=400&h=250&fit=crop"
      },
      {
        id: 4,
        title: "Digital Health Records: Streamlining Healthcare Operations Worldwide",
        content: "Implementation strategies and best practices for digital health record systems...",
        author: {
          name: "Vikram Malhotra",
          avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face",
          department: "IT Innovation",
          title: "Healthcare IT Director"
        },
        forum: {
          id: 4,
          name: "Healthcare Technology & Digital Innovation",
          category: "technology"
        },
        createdAt: "2024-07-17T14:30:00Z",
        lastActivity: "2024-07-22T16:45:00Z",
        views: 1654,
        likes: 98,
        replies: 42,
        shares: 16,
        trendingScore: 74,
        isHot: false,
        isPinned: false,
        tags: ["Digital Health", "EHR", "Technology", "Operations"],
        image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=250&fit=crop"
      },
      {
        id: 5,
        title: "Nursing Excellence Awards: Celebrating Our Healthcare Heroes",
        content: "Recognizing outstanding nursing professionals and their contributions to patient care...",
        author: {
          name: "Maria Santos",
          avatar: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=50&h=50&fit=crop&crop=face",
          department: "Nursing Administration",
          title: "Chief Nursing Officer"
        },
        forum: {
          id: 5,
          name: "Nursing Excellence & Patient Care",
          category: "nursing"
        },
        createdAt: "2024-07-16T11:45:00Z",
        lastActivity: "2024-07-21T13:20:00Z",
        views: 2456,
        likes: 203,
        replies: 78,
        shares: 45,
        trendingScore: 89,
        isHot: true,
        isPinned: false,
        tags: ["Nursing", "Awards", "Recognition", "Patient Care"],
        image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&h=250&fit=crop"
      },
      {
        id: 6,
        title: "Telemedicine Best Practices: Lessons from Global Implementation",
        content: "Sharing insights and best practices from successful telemedicine implementations worldwide...",
        author: {
          name: "Dr. James Park",
          avatar: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=50&h=50&fit=crop&crop=face",
          department: "Telehealth",
          title: "Director of Telemedicine"
        },
        forum: {
          id: 4,
          name: "Healthcare Technology & Digital Innovation",
          category: "technology"
        },
        createdAt: "2024-07-15T16:20:00Z",
        lastActivity: "2024-07-20T10:15:00Z",
        views: 1387,
        likes: 87,
        replies: 35,
        shares: 12,
        trendingScore: 68,
        isHot: false,
        isPinned: false,
        tags: ["Telemedicine", "Best Practices", "Implementation", "Global Health"],
        image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=400&h=250&fit=crop"
      }
    ];

    // Sample statistics
    const statsData = {
      totalDiscussions: 1247,
      activeDiscussions: 89,
      totalViews: 45670,
      totalLikes: 2890,
      totalReplies: 5634,
      trendingTopics: [
        { name: "AI in Healthcare", count: 45, trend: "+23%" },
        { name: "Mental Health", count: 38, trend: "+18%" },
        { name: "Telemedicine", count: 32, trend: "+15%" },
        { name: "Surgical Innovation", count: 28, trend: "+12%" },
        { name: "Digital Health", count: 24, trend: "+8%" }
      ]
    };

    // Simulate loading
    setTimeout(() => {
      setDiscussions(discussionsData);
      setStats(statsData);
      setLoading(false);
    }, 1000);
  }, [timeFilter, categoryFilter]);

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInHours = Math.floor((now - time) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return time.toLocaleDateString();
  };

  const handleDiscussionClick = (discussionId) => {
    navigate(`/forums/thread/${discussionId}`);
  };

  const filteredDiscussions = discussions.filter(discussion => {
    const now = new Date();
    const discussionDate = new Date(discussion.lastActivity);
    const diffInDays = Math.floor((now - discussionDate) / (1000 * 60 * 60 * 24));

    let timeMatch = true;
    switch (timeFilter) {
      case 'day':
        timeMatch = diffInDays < 1;
        break;
      case 'week':
        timeMatch = diffInDays < 7;
        break;
      case 'month':
        timeMatch = diffInDays < 30;
        break;
      default:
        timeMatch = true;
    }

    const categoryMatch = categoryFilter === 'all' || discussion.forum.category === categoryFilter;
    return timeMatch && categoryMatch;
  });

  const sortedDiscussions = [...filteredDiscussions].sort((a, b) => {
    switch (sortBy) {
      case 'trending':
        return b.trendingScore - a.trendingScore;
      case 'popular':
        return b.likes - a.likes;
      case 'views':
        return b.views - a.views;
      case 'recent':
        return new Date(b.lastActivity) - new Date(a.lastActivity);
      default:
        return 0;
    }
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-apollo-gray">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apollo-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading popular discussions...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      {/* Page Header */}
      <div className="bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 text-white py-8 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <FireSolidIcon className="w-12 h-12 sm:w-16 sm:h-16 opacity-80" />
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">Popular Discussions</h1>
            <p className="text-base sm:text-lg lg:text-xl opacity-90 max-w-2xl mx-auto px-4">
              Discover trending topics, hot discussions, and the most engaging conversations in our healthcare community
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Filters and Search */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-lg w-full lg:w-auto">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search popular discussions..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none transition-all duration-200"
              />
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4 w-full lg:w-auto">
              {/* Time Filter */}
              <div className="relative w-full sm:w-auto">
                <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  value={timeFilter}
                  onChange={(e) => setTimeFilter(e.target.value)}
                  className="w-full sm:w-auto pl-10 pr-8 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none transition-all duration-200 bg-white"
                >
                  <option value="day">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="all">All Time</option>
                </select>
              </div>

              {/* Category Filter */}
              <div className="relative w-full sm:w-auto">
                <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full sm:w-auto pl-10 pr-8 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none transition-all duration-200 bg-white"
                >
                  <option value="all">All Categories</option>
                  <option value="medical">Medical Research</option>
                  <option value="wellness">Staff Wellness</option>
                  <option value="technology">Healthcare Tech</option>
                  <option value="nursing">Nursing Care</option>
                  <option value="general">General Discussion</option>
                </select>
              </div>

              {/* Sort Filter */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full sm:w-auto px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none transition-all duration-200 bg-white"
              >
                <option value="trending">🔥 Trending</option>
                <option value="popular">❤️ Most Liked</option>
                <option value="views">👁️ Most Viewed</option>
                <option value="recent">🕒 Most Recent</option>
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Featured Discussion */}
            {sortedDiscussions.length > 0 && (
              <div className="mb-6 sm:mb-8">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <StarSolidIcon className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-yellow-500" />
                  Featured Discussion
                </h2>
                <div
                  className="bg-white rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-300 overflow-hidden border border-yellow-200 cursor-pointer"
                  onClick={() => handleDiscussionClick(sortedDiscussions[0].id)}
                >
                  <div className="relative">
                    <img
                      src={sortedDiscussions[0].image}
                      alt={sortedDiscussions[0].title}
                      className="w-full h-48 sm:h-56 lg:h-64 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                    <div className="absolute top-4 left-4 flex items-center space-x-2">
                      <span className="bg-yellow-500 text-white text-xs px-2 sm:px-3 py-1 rounded-full font-medium flex items-center">
                        <StarSolidIcon className="w-3 h-3 mr-1" />
                        Featured
                      </span>
                      {sortedDiscussions[0].isHot && (
                        <span className="bg-red-500 text-white text-xs px-2 sm:px-3 py-1 rounded-full font-medium flex items-center">
                          <FireSolidIcon className="w-3 h-3 mr-1" />
                          Hot
                        </span>
                      )}
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-2">
                        {sortedDiscussions[0].title}
                      </h3>
                      <p className="text-white/90 text-sm line-clamp-2 mb-3">
                        {sortedDiscussions[0].content}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-white/80 text-xs sm:text-sm">
                        <div className="flex items-center space-x-1">
                          <ArrowTrendingUpIcon className="w-4 h-4" />
                          <span>Trending #{sortedDiscussions[0].trendingScore}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <EyeIcon className="w-4 h-4" />
                          <span>{sortedDiscussions[0].views.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <HeartIcon className="w-4 h-4" />
                          <span>{sortedDiscussions[0].likes}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <ChatBubbleOvalLeftIcon className="w-4 h-4" />
                          <span>{sortedDiscussions[0].replies}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Popular Discussions List */}
            <div className="mb-6 sm:mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
                <FireSolidIcon className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-orange-500" />
                Trending Discussions ({sortedDiscussions.length})
              </h2>
              
              <div className="space-y-4 sm:space-y-6">
                {sortedDiscussions.slice(1).map((discussion, index) => (
                  <div
                    key={discussion.id}
                    className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-4 sm:p-6 border border-gray-100 hover:border-apollo-accent/30 cursor-pointer"
                    onClick={() => handleDiscussionClick(discussion.id)}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start space-y-4 sm:space-y-0 sm:space-x-4">
                      {/* Ranking */}
                      <div className="flex-shrink-0 text-center">
                        <div className="w-10 h-10 bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                          {index + 2}
                        </div>
                      </div>

                      {/* Discussion Image */}
                      <div className="flex-shrink-0">
                        <img
                          src={discussion.image}
                          alt={discussion.title}
                          className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover"
                        />
                      </div>

                      {/* Discussion Content */}
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          {discussion.isHot && (
                            <span className="bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full flex items-center">
                              <FireSolidIcon className="w-3 h-3 mr-1" />
                              Hot
                            </span>
                          )}
                          {discussion.isPinned && (
                            <span className="bg-apollo-accent text-white text-xs px-2 py-1 rounded-full">📌 Pinned</span>
                          )}
                          <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                            {discussion.forum.name}
                          </span>
                          <div className="flex items-center text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                            <ArrowTrendingUpIcon className="w-3 h-3 mr-1" />
                            <span>Trending {discussion.trendingScore}</span>
                          </div>
                        </div>
                        
                        <h3 className="text-lg sm:text-xl font-bold text-apollo-primary mb-2 hover:text-apollo-accent transition-colors line-clamp-2">
                          {discussion.title}
                        </h3>
                        
                        <p className="text-gray-600 mb-4 line-clamp-2 text-sm sm:text-base">
                          {discussion.content}
                        </p>

                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                          <div className="flex items-center space-x-2">
                            <img
                              src={discussion.author.avatar}
                              alt={discussion.author.name}
                              className="w-8 h-8 rounded-full"
                            />
                            <div>
                              <div className="text-sm font-medium text-gray-900">{discussion.author.name}</div>
                              <div className="text-xs text-gray-500">{discussion.author.department}</div>
                            </div>
                          </div>

                          <div className="flex items-center space-x-4 sm:space-x-6 text-sm text-gray-500">
                            <div className="flex items-center space-x-1">
                              <EyeIcon className="w-4 h-4" />
                              <span>{discussion.views.toLocaleString()}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <HeartIcon className="w-4 h-4" />
                              <span>{discussion.likes}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <ChatBubbleOvalLeftIcon className="w-4 h-4" />
                              <span>{discussion.replies}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <ClockIcon className="w-4 h-4" />
                              <span>{formatTimeAgo(discussion.lastActivity)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Tags */}
                        <div className="flex items-center space-x-2 mt-3">
                          {discussion.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="bg-apollo-accent/10 text-apollo-accent text-xs px-2 py-1 rounded-full">
                              #{tag}
                            </span>
                          ))}
                          {discussion.tags.length > 3 && (
                            <span className="text-xs text-gray-500">+{discussion.tags.length - 3} more</span>
                          )}
                        </div>
                      </div>

                      <ChevronRightIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Load More */}
            <div className="text-center">
              <button className="bg-white border border-gray-300 text-gray-700 px-6 sm:px-8 py-3 rounded-xl hover:bg-gray-50 transition-colors font-medium text-sm sm:text-base">
                Load More Discussions
              </button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6 lg:space-y-8">
            {/* Community Stats */}
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <ChartBarIcon className="w-5 h-5 mr-2 text-apollo-accent" />
                Community Stats
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 text-sm sm:text-base">Total Discussions</span>
                  <span className="font-semibold text-apollo-primary text-sm sm:text-base">{stats.totalDiscussions?.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 text-sm sm:text-base">Active Today</span>
                  <span className="font-semibold text-green-600 text-sm sm:text-base">{stats.activeDiscussions}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 text-sm sm:text-base">Total Views</span>
                  <span className="font-semibold text-apollo-primary text-sm sm:text-base">{stats.totalViews?.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 text-sm sm:text-base">Total Likes</span>
                  <span className="font-semibold text-red-500 text-sm sm:text-base">{stats.totalLikes?.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 text-sm sm:text-base">Total Replies</span>
                  <span className="font-semibold text-apollo-primary text-sm sm:text-base">{stats.totalReplies?.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Trending Topics */}
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <ArrowTrendingUpIcon className="w-5 h-5 mr-2 text-green-500" />
                Trending Topics
              </h3>
              <div className="space-y-3">
                {stats.trendingTopics?.map((topic, index) => (
                  <div key={topic.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-gradient-to-r from-green-400 to-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 text-sm">{topic.name}</div>
                        <div className="text-xs text-gray-500">{topic.count} discussions</div>
                      </div>
                    </div>
                    <div className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                      {topic.trend}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/forums')}
                  className="w-full bg-gradient-to-r from-apollo-accent to-blue-600 text-white py-3 px-4 rounded-xl hover:from-apollo-accent/90 hover:to-blue-600/90 transition-colors font-medium flex items-center justify-center space-x-2 text-sm sm:text-base"
                >
                  <ChatBubbleOvalLeftIcon className="w-5 h-5" />
                  <span>Browse All Forums</span>
                </button>
                <button className="w-full border border-apollo-accent text-apollo-accent py-3 px-4 rounded-xl hover:bg-apollo-accent/5 transition-colors font-medium text-sm sm:text-base">
                  Start New Discussion
                </button>
                <button className="w-full border border-gray-300 text-gray-700 py-3 px-4 rounded-xl hover:bg-gray-50 transition-colors font-medium text-sm sm:text-base">
                  View My Posts
                </button>
              </div>
            </div>

            {/* Top Contributors */}
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <UserGroupIcon className="w-5 h-5 mr-2 text-purple-500" />
                Top Contributors
              </h3>
              <div className="space-y-3">
                {discussions.slice(0, 5).map((discussion, index) => (
                  <div key={discussion.author.name} className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-gradient-to-r from-purple-400 to-pink-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                      {index + 1}
                    </div>
                    <img
                      src={discussion.author.avatar}
                      alt={discussion.author.name}
                      className="w-8 h-8 rounded-full"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{discussion.author.name}</div>
                      <div className="text-xs text-gray-500">{discussion.author.department}</div>
                    </div>
                    <div className="text-xs font-medium text-purple-600">
                      {discussion.likes} likes
                    </div>
                  </div>))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PopularDiscussions;
